import React, { useState, useEffect, useCallback } from 'react';
import { ChartBar, Plus, Trash, Trophy, X, PencilSimple, Printer, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { averageAPI } from '../../utils/api';

export const AveragesPanel = ({ show, isAdmin }) => {
  const [averages, setAverages] = useState([]);
  const [selectedAvg, setSelectedAvg] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAvg, setEditingAvg] = useState(null);

  const loadAverages = useCallback(async () => {
    try {
      const { data } = await averageAPI.list(show.id);
      setAverages(data);
    } catch {
      /* silent */
    }
  }, [show.id]);

  useEffect(() => { loadAverages(); }, [loadAverages]);

  const handleSelectAvg = async (avg) => {
    setSelectedAvg(avg);
    setResultsLoading(true);
    try {
      const { data } = await averageAPI.results(show.id, avg.id);
      setResults(data);
    } catch {
      toast.error('Failed to load average results');
    } finally {
      setResultsLoading(false);
    }
  };

  const handleDelete = async (avgId) => {
    try {
      await averageAPI.delete(show.id, avgId);
      toast.success('Average deleted');
      if (selectedAvg?.id === avgId) { setSelectedAvg(null); setResults(null); }
      loadAverages();
    } catch {
      toast.error('Failed to delete average');
    }
  };

  const classMap = {};
  (show.classes || []).forEach(c => { classMap[c.id] = c; });

  return (
    <div className="mt-8" data-testid="averages-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ChartBar size={24} weight="duotone" className="text-[#AF52DE]" />
          <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Averages</h2>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            data-testid="create-average-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#AF52DE]/10 border border-[#AF52DE]/30 text-[#AF52DE] font-semibold rounded-md hover:bg-[#AF52DE]/20 transition-colors text-sm"
          >
            <Plus size={14} weight="bold" /> New Average
          </button>
        )}
      </div>

      {averages.length === 0 && !showCreate ? (
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-8 text-center">
          <ChartBar size={40} className="text-[#262626] mx-auto mb-3" />
          <p className="text-[#9E9E9E] text-sm">No averages configured for this show</p>
          {isAdmin && <p className="text-[#555] text-xs mt-1">Click "New Average" to combine times across classes</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {averages.map((avg) => (
            <div
              key={avg.id}
              data-testid={`average-card-${avg.id}`}
              className={`bg-[#161616] border rounded-lg p-5 cursor-pointer transition-all hover:-translate-y-0.5 ${
                selectedAvg?.id === avg.id ? 'border-[#AF52DE]' : 'border-[#262626] hover:border-[#AF52DE]/50'
              }`}
              onClick={() => handleSelectAvg(avg)}
            >
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{avg.name}</h4>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingAvg(avg); }}
                      data-testid={`edit-avg-${avg.id}`}
                      className="text-[#AF52DE]/60 hover:text-[#AF52DE] transition-colors p-1"
                      title="Edit places, added money, entry fee, pool override, or per-place payouts"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(avg.id); }}
                      data-testid={`delete-avg-${avg.id}`}
                      className="text-[#FF3B30]/60 hover:text-[#FF3B30] transition-colors p-1"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#9E9E9E] mb-2">{avg.selected_class_ids.length} classes combined</p>
              <div className="space-y-1">
                {avg.selected_class_ids.map(cid => (
                  <p key={cid} className="text-xs text-[#AF52DE]/70">
                    {classMap[cid]?.name || cid}
                  </p>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-[#262626] flex justify-between text-xs">
                <span className="text-[#9E9E9E]">
                  {avg.payout_type === 'PRIZES_ONLY' ? 'Prizes Only' : 'From Entries'}
                </span>
                <span className="text-white font-semibold">{avg.places_paid} places</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results Display */}
      {selectedAvg && (
        <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden" data-testid="average-results">
          <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{selectedAvg.name} — Results</h3>
              {results && (
                <p className="text-xs text-[#9E9E9E] mt-1">
                  {results.total_eligible} eligible rider{results.total_eligible !== 1 ? 's' : ''}
                  {results.total_pool > 0 && (
                    <span>
                      {' '}&middot; Pool:{' '}
                      <span className="text-[#34C759]">${results.total_pool.toFixed(0)}</span>
                      {results.pool_overridden && (
                        <span className="ml-1 text-[9px] uppercase tracking-wider bg-[#AF52DE]/20 text-[#AF52DE] px-1 py-0.5 rounded font-bold" data-testid="pool-override-badge">Override</span>
                      )}
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* v256 — Print / PDF the average results */}
              {(() => {
                const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
                const pdfUrl = `${BACKEND}/api/shows/${show.id}/averages/${selectedAvg.id}/results.pdf`;
                const canPrint = results && Array.isArray(results.results) && results.results.length > 0;
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => window.open(`${pdfUrl}?inline=1`, '_blank', 'noopener')}
                      disabled={!canPrint}
                      data-testid="print-avg-results"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#0096FF]/10 border border-[#0096FF]/40 hover:bg-[#0096FF]/20 text-[#0096FF] rounded-md text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Open a printable PDF in a new tab"
                    ><Printer size={13} weight="bold" /> Print</button>
                    <button
                      type="button"
                      onClick={() => window.open(pdfUrl, '_blank', 'noopener')}
                      disabled={!canPrint}
                      data-testid="download-avg-results-pdf"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#161616] border border-[#333] hover:border-[#0096FF] text-white rounded-md text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Download the average results as PDF"
                    ><FilePdf size={13} weight="bold" /> PDF</button>
                  </>
                );
              })()}
              <button onClick={() => { setSelectedAvg(null); setResults(null); }} className="text-[#9E9E9E] hover:text-white p-1">
                <X size={18} />
              </button>
            </div>
          </div>

          {resultsLoading ? (
            <div className="p-8 text-center"><div className="shimmer h-8 w-8 rounded-full mx-auto"></div></div>
          ) : results && Array.isArray(results.results) && results.results.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#9E9E9E] text-xs border-b border-[#262626]">
                    <th className="px-4 py-3 text-left w-12">Place</th>
                    {results.division_type && results.division_type !== 'NONE' && (
                      <th className="px-4 py-3 text-left w-14">Div</th>
                    )}
                    <th className="px-4 py-3 text-left">Rider</th>
                    {(results.selected_classes || []).map(cls => (
                      <th key={cls.id} className="px-4 py-3 text-right">{cls.name}</th>
                    ))}
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right">Average</th>
                    {results.total_pool > 0 && <th className="px-4 py-3 text-right">Payout</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((r) => (
                    <tr
                      key={`${r.rider_id || r.rider_name}-${r.horse_name || ''}`}
                      data-testid={`avg-result-${r.place}`}
                      className={`border-b border-[#262626]/50 ${
                        r.place === 1 ? 'bg-[#FFD700]/5' : r.place === 2 ? 'bg-[#C0C0C0]/5' : r.place === 3 ? 'bg-[#CD7F32]/5' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <span className={`font-black text-base ${
                          r.place === 1 ? 'text-[#FFD700]' : r.place === 2 ? 'text-[#C0C0C0]' : r.place === 3 ? 'text-[#CD7F32]' : 'text-[#9E9E9E]'
                        }`}>
                          {r.tied ? `T-${r.place}` : r.place}
                        </span>
                      </td>
                      {results.division_type && results.division_type !== 'NONE' && (
                        <td className="px-4 py-3">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-[#0096FF]">{r.division || '—'}</span>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <p className="text-white font-semibold">{r.rider_name}</p>
                        {r.horse_name && <p className="text-[#9E9E9E] text-xs">{r.horse_name}</p>}
                      </td>
                      {/* v253 — Defensive numeric renders so no missing
                          field ever blank-screens the panel. */}
                      {(results.selected_classes || []).map(cls => {
                        const t = r.times && r.times[cls.id];
                        return (
                          <td key={cls.id} className="px-4 py-3 text-right text-white font-mono text-xs">
                            {typeof t === 'number' ? t.toFixed(3) : '—'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-right text-white font-mono font-bold">{typeof r.total_time === 'number' ? r.total_time.toFixed(3) : '—'}</td>
                      <td className="px-4 py-3 text-right text-[#AF52DE] font-mono font-bold">{typeof r.average_time === 'number' ? r.average_time.toFixed(3) : '—'}</td>
                      {results.total_pool > 0 && (
                        <td className="px-4 py-3 text-right font-semibold">
                          <span className={r.payout_overridden ? 'text-[#AF52DE]' : 'text-[#34C759]'}>
                            {typeof r.payout === 'number' && r.payout > 0 ? `$${r.payout.toFixed(0)}` : '—'}
                          </span>
                          {r.payout_overridden && (
                            <span className="ml-1 text-[8px] uppercase tracking-wider text-[#AF52DE]/80" title="Manually overridden">edit</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : results ? (
            <div className="p-8 text-center">
              <Trophy size={32} className="text-[#262626] mx-auto mb-2" />
              <p className="text-[#9E9E9E] text-sm">No eligible riders yet</p>
              <p className="text-[#555] text-xs mt-1">Riders need completed runs in all selected classes to qualify</p>
            </div>
          ) : null}
        </div>
      )}

      {/* Create Average Modal */}
      {showCreate && (
        <CreateAverageModal
          show={show}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadAverages(); }}
        />
      )}

      {/* v253 — Edit modal: places, added money, entry fee, payback %,
          plus pool override and per-place payout overrides. */}
      {editingAvg && (
        <EditAverageModal
          show={show}
          avg={editingAvg}
          currentResults={selectedAvg && selectedAvg.id === editingAvg.id ? results : null}
          onClose={() => setEditingAvg(null)}
          onSaved={async () => {
            setEditingAvg(null);
            await loadAverages();
            if (selectedAvg?.id === editingAvg.id) {
              try {
                const { data } = await averageAPI.results(show.id, editingAvg.id);
                setResults(data);
              } catch { /* ignore */ }
            }
          }}
        />
      )}
    </div>
  );
};


const CreateAverageModal = ({ show, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [payoutType, setPayoutType] = useState('FROM_ENTRIES');
  const [placesPaid, setPlacesPaid] = useState(4);
  const [addedMoney, setAddedMoney] = useState(0);
  const [paybackPercent, setPaybackPercent] = useState(70);
  const [entryFee, setEntryFee] = useState(0);
  const [divisionType, setDivisionType] = useState('NONE');
  const [saving, setSaving] = useState(false);

  const showDates = [];
  if (show.start_date && show.end_date) {
    const start = new Date(show.start_date + 'T00:00:00');
    const end = new Date(show.end_date + 'T00:00:00');
    const current = new Date(start);
    while (current <= end) {
      showDates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const toggleClass = (classId) => {
    setSelectedClassIds(prev =>
      prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]
    );
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Enter a name for the average'); return; }
    if (selectedClassIds.length < 2) { toast.error('Select at least 2 classes'); return; }
    setSaving(true);
    try {
      await averageAPI.create(show.id, {
        name: name.trim(),
        selected_class_ids: selectedClassIds,
        payout_type: payoutType,
        places_paid: Number(placesPaid),
        added_money: Number(addedMoney),
        payback_percent: Number(paybackPercent),
        entry_fee: Number(entryFee),
        division_type: divisionType,
      });
      toast.success('Average created!');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create average');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#161616] border border-[#262626] rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#262626] flex items-center justify-between sticky top-0 bg-[#161616] z-10">
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Create Average</h3>
          <button onClick={onClose} className="text-[#9E9E9E] hover:text-white"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Average Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Open Barrels Fri/Sat Average"
              data-testid="avg-name-input"
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm placeholder:text-[#555] focus:border-[#AF52DE] focus:outline-none transition-colors"
            />
          </div>

          {/* Class Selection */}
          <div>
            <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">
              Select Classes to Average
              <span className="text-[#555] font-normal ml-2">(min 2)</span>
            </label>
            <p className="text-xs text-[#555] mb-3">Pick the classes whose times will be combined and averaged</p>

            {(() => {
              const hasScheduled = show.classes.some(c => c.scheduled_day || c.scheduled_date);
              if (hasScheduled && showDates.length > 1) {
                return showDates.map(date => {
                  const dayClasses = show.classes.filter(c => c.scheduled_day === date || c.scheduled_date === date);
                  if (dayClasses.length === 0) return null;
                  const dateObj = new Date(date + 'T00:00:00');
                  const dayLabel = `${dayNames[dateObj.getDay()]} (${dateObj.toLocaleDateString()})`;
                  return (
                    <div key={date} className="mb-3">
                      <p className="text-xs font-semibold text-[#AF52DE]/70 mb-1.5">{dayLabel}</p>
                      {dayClasses.map(cls => (
                        <ClassCheckbox key={cls.id} cls={cls} selected={selectedClassIds.includes(cls.id)} onToggle={toggleClass} />
                      ))}
                    </div>
                  );
                });
              }
              return (
                <div className="space-y-1">
                  {show.classes.map(cls => (
                    <ClassCheckbox key={cls.id} cls={cls} selected={selectedClassIds.includes(cls.id)} onToggle={toggleClass} />
                  ))}
                </div>
              );
            })()}

            <p className="text-xs text-[#9E9E9E] mt-2">
              {selectedClassIds.length} class{selectedClassIds.length !== 1 ? 'es' : ''} selected
            </p>
          </div>

          {/* Payout Type */}
          <div>
            <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Payout Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPayoutType('FROM_ENTRIES')}
                data-testid="payout-type-entries"
                className={`p-3 rounded-lg border text-sm font-semibold transition-all ${
                  payoutType === 'FROM_ENTRIES'
                    ? 'bg-[#AF52DE]/10 border-[#AF52DE] text-[#AF52DE]'
                    : 'bg-[#0A0A0A] border-[#262626] text-[#9E9E9E] hover:border-[#AF52DE]/50'
                }`}
              >
                From Entry Fees
              </button>
              <button
                onClick={() => setPayoutType('PRIZES_ONLY')}
                data-testid="payout-type-prizes"
                className={`p-3 rounded-lg border text-sm font-semibold transition-all ${
                  payoutType === 'PRIZES_ONLY'
                    ? 'bg-[#AF52DE]/10 border-[#AF52DE] text-[#AF52DE]'
                    : 'bg-[#0A0A0A] border-[#262626] text-[#9E9E9E] hover:border-[#AF52DE]/50'
                }`}
              >
                Prizes Only
              </button>
            </div>
          </div>

          {/* Entry Fee (for averages with own fee) */}
          {payoutType === 'FROM_ENTRIES' && (
            <div>
              <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Average Entry Fee ($)</label>
              <input
                type="number"
                value={entryFee}
                onChange={(e) => setEntryFee(e.target.value)}
                data-testid="avg-entry-fee"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#AF52DE] focus:outline-none"
                placeholder="0"
              />
              <p className="text-[10px] text-[#555] mt-1">Leave 0 to calculate from class entry fees</p>
            </div>
          )}

          {/* Division Type */}
          <div>
            <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Divisions</label>
            <div className="grid grid-cols-3 gap-2">
              {['NONE', '2D', '3D', '4D'].map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDivisionType(d)}
                  className={`p-2 rounded-lg border text-xs font-semibold transition-all ${
                    divisionType === d ? 'bg-[#AF52DE]/10 border-[#AF52DE] text-[#AF52DE]' : 'bg-[#0A0A0A] border-[#262626] text-[#9E9E9E]'
                  }`}
                >
                  {d === 'NONE' ? 'Overall Only' : d}
                </button>
              ))}
            </div>
          </div>

          {/* Places Paid & Payback */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Places Paid</label>
              <select
                value={placesPaid}
                onChange={(e) => setPlacesPaid(Number(e.target.value))}
                data-testid="avg-places-paid"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#AF52DE] focus:outline-none"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            {payoutType === 'FROM_ENTRIES' && (
              <div>
                <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Payback %</label>
                <input
                  type="number"
                  value={paybackPercent}
                  onChange={(e) => setPaybackPercent(e.target.value)}
                  data-testid="avg-payback-pct"
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#AF52DE] focus:outline-none"
                />
              </div>
            )}
          </div>

          {/* Added Money */}
          <div>
            <label className="text-sm font-semibold text-[#9E9E9E] block mb-2">Added Money ($)</label>
            <input
              type="number"
              value={addedMoney}
              onChange={(e) => setAddedMoney(e.target.value)}
              data-testid="avg-added-money"
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#AF52DE] focus:outline-none"
              placeholder="0"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#262626] flex gap-3 justify-end sticky bottom-0 bg-[#161616]">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || selectedClassIds.length < 2 || !name.trim()}
            data-testid="avg-create-submit"
            className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-[#AF52DE] hover:bg-[#AF52DE]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Creating...' : 'Create Average'}
          </button>
        </div>
      </div>
    </div>
  );
};


const ClassCheckbox = ({ cls, selected, onToggle }) => (
  <div
    onClick={() => onToggle(cls.id)}
    className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors mb-1 ${
      selected ? 'bg-[#AF52DE]/10 border border-[#AF52DE]/30' : 'bg-[#0A0A0A] border border-transparent hover:border-[#262626]'
    }`}
    data-testid={`avg-class-${cls.id}`}
  >
    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
      selected ? 'bg-[#AF52DE] border-[#AF52DE]' : 'border-[#555]'
    }`}>
      {selected && <span className="text-white text-[10px] font-bold">&#10003;</span>}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white text-sm font-semibold truncate">{cls.name}</p>
    </div>
    <span className="text-xs text-[#9E9E9E]">${cls.entry_fee}</span>
  </div>
);



// v253 — Edit an existing average's config + optional overrides.
const EditAverageModal = ({ show, avg, currentResults, onClose, onSaved }) => {
  const [name, setName] = useState(avg.name || '');
  const [placesPaid, setPlacesPaid] = useState(avg.places_paid || 4);
  const [addedMoney, setAddedMoney] = useState(avg.added_money || 0);
  const [entryFee, setEntryFee] = useState(avg.entry_fee || 0);
  const [paybackPercent, setPaybackPercent] = useState(avg.payback_percent || 70);
  const [poolOverride, setPoolOverride] = useState(
    avg.total_pool_override != null && avg.total_pool_override !== '' ? String(avg.total_pool_override) : ''
  );
  const [payoutOverrides, setPayoutOverrides] = useState(() => {
    const o = avg.payout_overrides || {};
    const out = {};
    Object.keys(o).forEach((k) => { out[k] = String(o[k]); });
    return out;
  });
  // v254 — division controls
  const [divisionType, setDivisionType] = useState((avg.division_type || 'NONE').toUpperCase());
  const [divisionPoolPct, setDivisionPoolPct] = useState(() => {
    const p = avg.division_pool_pct || {};
    const out = {};
    ['1D', '2D', '3D', '4D'].forEach((d) => { out[d] = p[d] != null ? String(p[d]) : ''; });
    return out;
  });
  const [saving, setSaving] = useState(false);

  const isDivisional = ['2D', '3D', '4D'].includes(divisionType);
  const divNames = divisionType === '4D' ? ['1D', '2D', '3D', '4D']
    : divisionType === '3D' ? ['1D', '2D', '3D']
    : divisionType === '2D' ? ['1D', '2D'] : [];
  const places = Array.from({ length: Number(placesPaid) || 0 }, (_, i) => i + 1);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanedPayouts = {};
      Object.entries(payoutOverrides).forEach(([k, v]) => {
        const trimmed = (v ?? '').toString().trim();
        if (trimmed === '') return;
        const num = Number(trimmed);
        if (!isNaN(num) && num >= 0) cleanedPayouts[k] = num;
      });
      const cleanedDivPct = {};
      if (isDivisional) {
        divNames.forEach((d) => {
          const v = (divisionPoolPct[d] ?? '').toString().trim();
          if (v !== '') {
            const n = Number(v);
            if (!isNaN(n) && n >= 0) cleanedDivPct[d] = n;
          }
        });
      }
      const payload = {
        name: name.trim(),
        places_paid: Number(placesPaid),
        added_money: Number(addedMoney) || 0,
        entry_fee: Number(entryFee) || 0,
        payback_percent: Number(paybackPercent) || 0,
        total_pool_override: poolOverride.trim() === '' ? null : Number(poolOverride),
        payout_overrides: cleanedPayouts,
        division_type: divisionType,
        division_pool_pct: cleanedDivPct,
      };
      await averageAPI.update(show.id, avg.id, payload);
      toast.success('Average updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-avg-modal">
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between sticky top-0 bg-[#0A0A0A]">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Edit &quot;{avg.name}&quot;</h3>
          <button onClick={onClose} className="text-[#9E9E9E] hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#9E9E9E] mb-1">Name</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="edit-avg-name"
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#AF52DE]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9E9E9E] mb-1">Places Paid</label>
              <input
                type="number" min="1" max="15" value={placesPaid}
                onChange={(e) => setPlacesPaid(e.target.value)}
                data-testid="edit-avg-places-paid"
                className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#AF52DE]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9E9E9E] mb-1">Payback %</label>
              <input
                type="number" min="0" max="100" step="0.1" value={paybackPercent}
                onChange={(e) => setPaybackPercent(e.target.value)}
                data-testid="edit-avg-payback"
                className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#AF52DE]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9E9E9E] mb-1">Added Money ($)</label>
              <input
                type="number" min="0" step="1" value={addedMoney}
                onChange={(e) => setAddedMoney(e.target.value)}
                data-testid="edit-avg-added-money"
                className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#AF52DE]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9E9E9E] mb-1">Entry Fee ($)</label>
              <input
                type="number" min="0" step="1" value={entryFee}
                onChange={(e) => setEntryFee(e.target.value)}
                data-testid="edit-avg-entry-fee"
                className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#AF52DE]"
              />
            </div>
          </div>

          <div className="pt-3 border-t border-[#262626]">
            <label className="block text-xs uppercase tracking-wider text-[#AF52DE] mb-1">Manual Pool Override</label>
            <input
              type="number" min="0" step="1" value={poolOverride}
              onChange={(e) => setPoolOverride(e.target.value)}
              placeholder="Leave blank to auto-compute from entries"
              data-testid="edit-avg-pool-override"
              className="w-full px-3 py-2 bg-[#161616] border border-[#AF52DE]/30 rounded-md text-white text-sm focus:outline-none focus:border-[#AF52DE]"
            />
            <p className="text-[10px] text-[#9E9E9E] mt-1">
              {currentResults && (
                <span>Auto-computed pool = <span className="text-[#34C759]">${(currentResults.computed_pool || 0).toFixed(0)}</span>. </span>
              )}
              Type a number to override; leave blank to use the auto value.
            </p>
          </div>

          {/* v254 — Division type + pool split. NONE = overall ranking. */}
          <div className="pt-3 border-t border-[#262626]">
            <label className="block text-xs uppercase tracking-wider text-[#0096FF] mb-1">Division Type</label>
            <select
              value={divisionType}
              onChange={(e) => setDivisionType(e.target.value)}
              data-testid="edit-avg-division-type"
              className="w-full px-3 py-2 bg-[#161616] border border-[#0096FF]/30 rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
            >
              <option value="NONE">Overall (no divisions)</option>
              <option value="2D">2D</option>
              <option value="3D">3D</option>
              <option value="4D">4D</option>
            </select>
            <p className="text-[10px] text-[#9E9E9E] mt-1">Riders bucket by AVERAGE time vs. the fastest average. Places + payouts assign within each division.</p>
            {isDivisional && (
              <div className="mt-3 space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#9E9E9E]">Pool % per Division <span className="text-[#555] normal-case">(blank = equal split)</span></p>
                {divNames.map((d) => (
                  <div key={d} className="flex items-center gap-2">
                    <span className="text-white text-sm font-bold w-10">{d}</span>
                    <div className="flex-1 relative">
                      <input
                        type="number" min="0" max="100" step="1"
                        value={divisionPoolPct[d] || ''}
                        onChange={(e) => setDivisionPoolPct({ ...divisionPoolPct, [d]: e.target.value })}
                        placeholder={`Auto: ${Math.round(100 / divNames.length)}%`}
                        data-testid={`edit-avg-div-pct-${d}`}
                        className="w-full pr-6 pl-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#555] text-xs">%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-[#262626]">
            <label className="block text-xs uppercase tracking-wider text-[#AF52DE] mb-2">Per-Place Payout Overrides</label>
            {isDivisional ? (
              <div className="space-y-3">
                {divNames.map((d) => (
                  <div key={d}>
                    <p className="text-[10px] uppercase tracking-wider text-[#0096FF] mb-1">{d}</p>
                    <div className="space-y-2">
                      {places.map((p) => {
                        const k = `${d}:${p}`;
                        const auto = currentResults && currentResults.payouts && currentResults.payouts[d] && typeof currentResults.payouts[d][p] === 'number'
                          ? `Auto: $${currentResults.payouts[d][p].toFixed(0)}`
                          : 'Auto';
                        return (
                          <div key={k} className="flex items-center gap-3">
                            <span className="text-white text-sm w-16">Place {p}</span>
                            <div className="flex-1 relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555] text-sm">$</span>
                              <input
                                type="number" min="0" step="1"
                                value={payoutOverrides[k] || ''}
                                onChange={(e) => setPayoutOverrides({ ...payoutOverrides, [k]: e.target.value })}
                                placeholder={auto}
                                data-testid={`edit-avg-payout-${d}-${p}`}
                                className="w-full pl-6 pr-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#AF52DE]"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {places.map((p) => (
                  <div key={p} className="flex items-center gap-3">
                    <span className="text-white text-sm font-bold w-16">Place {p}</span>
                    <div className="flex-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555] text-sm">$</span>
                      <input
                        type="number" min="0" step="1"
                        value={payoutOverrides[p] || ''}
                        onChange={(e) => setPayoutOverrides({ ...payoutOverrides, [p]: e.target.value })}
                        placeholder={
                          currentResults && currentResults.payouts && typeof currentResults.payouts[p] === 'number'
                            ? `Auto: $${currentResults.payouts[p].toFixed(0)}`
                            : 'Auto'
                        }
                        data-testid={`edit-avg-payout-${p}`}
                        className="w-full pl-6 pr-3 py-1.5 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#AF52DE]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-[#9E9E9E] mt-2">Any blank field = use auto amount from the pool split. Override wins when set.</p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#262626] flex justify-end gap-2 sticky bottom-0 bg-[#0A0A0A]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[#9E9E9E] hover:text-white text-sm font-semibold rounded-md"
          >Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            data-testid="edit-avg-save"
            className="px-5 py-2 bg-[#AF52DE] hover:bg-[#AF52DE]/90 text-white text-sm font-bold rounded-md disabled:opacity-50"
          >{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
};
